<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="fas fa-box"></i>
          {{ mode === 'create' ? 'Nouveau Produit' : 'Modifier Produit' }}
        </h2>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <!-- Stepper Navigation -->
        <div class="stepper-navigation">
          <div 
            v-for="(step, index) in steps" 
            :key="index" 
            class="step-item" 
            :class="getStepClass(index)"
            @click="goToStep(index)"
          >
            <div class="step-circle">
              <i v-if="isStepCompleted(index)" class="fas fa-check"></i>
              <i v-else-if="currentStep === index" :class="step.icon"></i>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <div class="step-content">
              <div class="step-title">{{ step.title }}</div>
              <div class="step-subtitle">{{ step.subtitle }}</div>
            </div>
            <div v-if="index < steps.length - 1" class="step-connector"></div>
          </div>
        </div>

        <form @submit.prevent="handleSubmit" class="form-container">
          <div class="step-content-wrapper">
          <!-- Étape 1: Informations Générales & Identification -->
          <div v-if="currentStep === 0" class="form-section active-step">
            <div class="step-header">
              <div class="step-icon-large">
                <i class="fas fa-info-circle"></i>
              </div>
              <div class="step-info">
                <h3 class="section-title">Informations Générales & Identification</h3>
                <p class="step-description">Renseignez les informations de base et les identifiants du produit</p>
              </div>
            </div>
            <div class="form-grid">
              <!-- CHAMPS OBLIGATOIRES EN PREMIER -->
              <div class="form-group">
                <label for="designation" class="required">Désignation</label>
                <input
                  type="text"
                  id="designation"
                  v-model="form.designation"
                  :class="{ error: errors.designation }"
                  placeholder="Nom du produit"
                  required
                />
                <span v-if="errors.designation" class="error-message">
                  {{ errors.designation }}
                </span>
              </div>
              
              <div class="form-group">
                <div class="label-row" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                  <label for="categorie" class="required" style="margin-bottom: 0;">Catégorie</label>
                  <button type="button" class="btn-quick-add" @click="openQuickCategoryModal" style="background: none; border: none; color: #10b981; font-size: 12px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; padding: 0;">
                    <i class="fas fa-plus-circle"></i> ajouter
                  </button>
                </div>
                <div class="premium-selector-wrapper" :class="{ 'has-selection': form.categorie, 'error': errors.categorie }">
                  <div class="selector-icon-container">
                    <i class="fas fa-layer-group selector-main-icon"></i>
                  </div>
                  <select
                    id="categorie"
                    v-model="form.categorie"
                    class="premium-select"
                    required
                    @change="onCategorieChange"
                  >
                    <option value="">✨ Sélectionner une catégorie</option>
                    <option v-if="loadingCategories" disabled>Chargement...</option>
                    <option 
                      v-for="cat in categories" 
                      :key="cat._id" 
                      :value="cat.nom"
                    >
                      {{ cat.emoji }} {{ cat.nom }} - {{ cat.description }}
                    </option>
                  </select>
                  <div class="selector-arrow-container">
                    <i class="fas fa-chevron-down selector-arrow"></i>
                  </div>
                  <div v-if="form.categorie" class="selection-indicator">
                    <i class="fas fa-check-circle"></i>
                  </div>
                  <div v-if="form.categorie" class="selection-badge">1</div>
                  <div class="selector-shine"></div>
                </div>
                <span v-if="errors.categorie" class="error-message">
                  {{ errors.categorie }}
                </span>
              </div>
              
              <!-- IMEI obligatoire pour GPS - placé en haut après catégorie -->
              <div class="form-group" v-if="form.categorie === 'GPS'">
                <label for="imei" class="required">IMEI</label>
                <input
                  type="text"
                  id="imei"
                  v-model="form.imei"
                  :class="{ error: errors.imei }"
                  placeholder="123456789012345"
                  pattern="[0-9]{15}"
                  maxlength="15"
                />
                <span v-if="errors.imei" class="error-message">
                  {{ errors.imei }}
                </span>
                <small class="help-text">15 chiffres requis pour les GPS</small>
              </div>
              
              <!-- ICCID obligatoire pour SIM - placé en haut après catégorie -->
              <div class="form-group" v-if="form.categorie === 'SIM'">
                <label for="iccid" class="required">ICCID</label>
                <input
                  type="text"
                  id="iccid"
                  v-model="form.iccid"
                  :class="{ error: errors.iccid }"
                  placeholder="89332404000000000000"
                  pattern="[0-9]{19,20}"
                  maxlength="20"
                />
                <span v-if="errors.iccid" class="error-message">
                  {{ errors.iccid }}
                </span>
                <small class="help-text">19-20 chiffres requis pour les SIM</small>
              </div>
              
              <!-- CHAMPS OPTIONNELS APRÈS -->
              <div class="form-group">
                <label for="marque">Marque</label>
                <input
                  type="text"
                  id="marque"
                  v-model="form.marque"
                  placeholder="Marque du produit"
                />
              </div>
              
              <div class="form-group">
                <label for="modele">Modèle</label>
                <input
                  type="text"
                  id="modele"
                  v-model="form.modele"
                  placeholder="Modèle du produit"
                />
              </div>
              
              <div class="form-group">
                <label for="description">Description</label>
                <textarea
                  id="description"
                  v-model="form.description"
                  placeholder="Description détaillée du produit"
                  rows="3"
                ></textarea>
              </div>
              
              <!-- Numéro de série masqué pour Accessoire, Capteur, Alimentation, Divers -->
              <div class="form-group" v-if="shouldShowNumeroSerie">
                <label for="numeroSerie">Numéro de Série</label>
                <input
                  type="text"
                  id="numeroSerie"
                  v-model="form.numeroSerie"
                  placeholder="Numéro de série du produit"
                />
              </div>
            </div>
          </div>

          <!-- Étape 2: Informations Financières -->
          <div v-if="currentStep === 1" class="form-section active-step">
            <div class="step-header">
              <div class="step-icon-large">
                <i class="fas fa-euro-sign"></i>
              </div>
              <div class="step-info">
                <h3 class="section-title">Informations Financières</h3>
                <p class="step-description">Définissez les prix et la TVA applicable</p>
              </div>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label for="prixAchat" class="required">Prix d'Achat (TND)</label>
                <input
                  type="number"
                  id="prixAchat"
                  v-model.number="form.prixAchat"
                  :class="{ error: errors.prixAchat }"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
                <span v-if="errors.prixAchat" class="error-message">
                  {{ errors.prixAchat }}
                </span>
              </div>
              
              <div class="form-group">
                <label for="prixVente" class="required">Prix de Vente HT (TND)</label>
                <input
                  type="number"
                  id="prixVente"
                  v-model.number="form.prixVente"
                  :class="{ error: errors.prixVente }"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                  @input="calculateTTC"
                />
                <span v-if="errors.prixVente" class="error-message">
                  {{ errors.prixVente }}
                </span>
              </div>
              
              <div class="form-group">
                <label for="tva">TVA (%)</label>
                <div class="tva-input-wrapper">
                  <input
                    type="number"
                    id="tva"
                    v-model.number="form.tva"
                    placeholder="19"
                    min="0"
                    max="100"
                    step="0.1"
                    @input="calculateTTC"
                  />
                  <span class="tva-symbol">%</span>
                </div>
                <small class="help-text">Taux de TVA applicable (ex: 19 pour 19%)</small>
              </div>
              
              <div class="form-group">
                <label>Prix de Vente TTC (TND)</label>
                <div class="calculated-value">
                  {{ formatPrice(prixVenteTTC) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Étape 3: Gestion de Stock -->
          <div v-if="currentStep === 2" class="form-section active-step">
            <div class="step-header">
              <div class="step-icon-large">
                <i class="fas fa-warehouse"></i>
              </div>
              <div class="step-info">
                <h3 class="section-title">Gestion de Stock</h3>
                <p class="step-description">Configurez les paramètres de stock et d'approvisionnement</p>
              </div>
            </div>
            <div class="form-grid">
              <!-- Champ Quantité visible uniquement pour Câble, Accessoire, Capteur, Alimentation, Divers -->
              <div class="form-group" v-if="shouldShowQuantite">
                <label for="quantite" class="required">Quantité</label>
                <input
                  type="number"
                  id="quantite"
                  v-model.number="form.quantite"
                  :class="{ error: errors.quantite }"
                  placeholder="1"
                  min="1"
                  required
                />
                <span v-if="errors.quantite" class="error-message">
                  {{ errors.quantite }}
                </span>
                <small class="help-text">Nombre d'unités à ajouter au stock</small>
              </div>
              
              <div class="form-group">
                <label for="seuilAlerte" class="required">Seuil d'Alerte</label>
                <input
                  type="number"
                  id="seuilAlerte"
                  v-model.number="form.seuilAlerte"
                  :class="{ error: errors.seuilAlerte }"
                  placeholder="5"
                  min="0"
                  required
                />
                <span v-if="errors.seuilAlerte" class="error-message">
                  {{ errors.seuilAlerte }}
                </span>
              </div>
              
              <div class="form-group">
                <label for="unite">Unité</label>
                <div class="premium-selector-wrapper" :class="{ 'has-selection': form.unite }">
                  <div class="selector-icon-container">
                    <i class="fas fa-ruler selector-main-icon"></i>
                  </div>
                  <select
                    id="unite"
                    v-model="form.unite"
                    class="premium-select"
                  >
                    <option value="">✨ Sélectionner une unité</option>
                    <option value="pièce">📦 Pièce - Unité</option>
                    <option value="mètre">📏 Mètre - Longueur</option>
                    <option value="kg">⚖️ Kilogramme - Poids</option>
                    <option value="litre">🪣 Litre - Volume</option>
                    <option value="lot">📋 Lot - Ensemble</option>
                  </select>
                  <div class="selector-arrow-container">
                    <i class="fas fa-chevron-down selector-arrow"></i>
                  </div>
                  <div v-if="form.unite" class="selection-indicator">
                    <i class="fas fa-check-circle"></i>
                  </div>
                  <div v-if="form.unite" class="selection-badge">1</div>
                  <div class="selector-shine"></div>
                </div>
              </div>
              
              <div class="form-group">
                <label for="emplacement">Emplacement Initial</label>
                <div class="premium-selector-wrapper" :class="{ 'has-selection': form.emplacement }">
                  <div class="selector-icon-container">
                    <i class="fas fa-map-marker-alt selector-main-icon"></i>
                  </div>
                  <select
                    id="emplacement"
                    v-model="form.emplacement"
                    class="premium-select"
                  >
                    <option value="">✨ Sélectionner un emplacement</option>
                    <option value="magasin_central">🏢 Magasin Central - Principal</option>
                    <option value="entrepot_a">🏭 Entrepôt A - Zone Nord</option>
                    <option value="entrepot_b">🏭 Entrepôt B - Zone Sud</option>
                  </select>
                  <div class="selector-arrow-container">
                    <i class="fas fa-chevron-down selector-arrow"></i>
                  </div>
                  <div v-if="form.emplacement" class="selection-indicator">
                    <i class="fas fa-check-circle"></i>
                  </div>
                  <div v-if="form.emplacement" class="selection-badge">1</div>
                  <div class="selector-shine"></div>
                </div>
              </div>
              
              <div class="form-group">
                <label for="fournisseur">Fournisseur</label>
                <input
                  type="text"
                  id="fournisseur"
                  v-model="form.fournisseur"
                  placeholder="Nom du fournisseur"
                />
              </div>
            </div>
          </div>
          </div>
          
          <!-- Navigation Steps -->
          <div class="step-navigation">
            <button 
              type="button" 
              @click="previousStep" 
              class="btn btn-secondary step-btn"
              :disabled="currentStep === 0"
              v-if="currentStep > 0"
            >
              <i class="fas fa-chevron-left"></i>
              Précédent
            </button>
            
            <div class="step-progress">
              <span class="progress-text">Étape {{ currentStep + 1 }} sur {{ steps.length }}</span>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
              </div>
            </div>
            
            <button 
              v-if="currentStep < steps.length - 1"
              type="button" 
              @click="nextStep" 
              class="btn btn-primary step-btn"
              :disabled="!canProceedToNext"
            >
              Suivant
              <i class="fas fa-chevron-right"></i>
            </button>
            
            <button 
              v-else
              type="submit" 
              @click="handleSubmit" 
              class="btn btn-success step-btn"
              :disabled="loading || !isFormValid"
            >
              <i v-if="loading" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-save"></i>
              {{ mode === 'create' ? 'Créer le Produit' : 'Modifier le Produit' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Quick Add Category Modal Overlay -->
    <div v-if="showQuickCategoryModal" class="modal-overlay sub-modal-overlay" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 2000;">
      <div class="modal-card card shadow-lg" style="width: 100%; max-width: 400px; background: white; border-radius: 12px; overflow: hidden;">
        <div class="card-header bg-success text-white d-flex justify-content-between align-items-center" style="padding: 15px 20px; background: linear-gradient(135deg, #10b981, #059669); color: white; display: flex; justify-content: space-between; align-items: center;">
          <h4 class="m-0" style="font-size: 16px; font-weight: 700; margin: 0;">Nouvelle Catégorie</h4>
          <button type="button" class="btn-close text-white border-0 bg-transparent" @click="closeQuickCategoryModal" style="cursor: pointer; color: white; font-size: 18px; border: none; background: none; padding: 0;">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="card-body" style="padding: 20px;">
          <form @submit.prevent="handleQuickCategorySubmit">
            <div class="form-group mb-3">
              <label class="form-label fw-semibold" style="font-size: 13px; font-weight: 600; color: #334155; display: block; margin-bottom: 6px;">Nom de la catégorie *</label>
              <input type="text" v-model="quickCategoryForm.nom" class="form-control" style="width: 100%; padding: 10px; border: 1px solid #cbd5e1; border-radius: 6px;" required placeholder="Ex: GPS V3" />
            </div>
            <div class="form-group mb-3">
              <label class="form-label fw-semibold" style="font-size: 13px; font-weight: 600; color: #334155; display: block; margin-bottom: 6px;">Emoji *</label>
              <input type="text" v-model="quickCategoryForm.emoji" class="form-control" style="width: 100%; padding: 10px; border: 1px solid #cbd5e1; border-radius: 6px;" required placeholder="Ex: 📡" />
            </div>
            <div class="form-group mb-3">
              <label class="form-label fw-semibold" style="font-size: 13px; font-weight: 600; color: #334155; display: block; margin-bottom: 6px;">Description</label>
              <textarea v-model="quickCategoryForm.description" class="form-control" style="width: 100%; padding: 10px; border: 1px solid #cbd5e1; border-radius: 6px; min-height: 80px;" placeholder="Optionnel..."></textarea>
            </div>
            <div class="d-flex gap-2 justify-content-end" style="display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px;">
              <button type="button" class="btn btn-secondary" @click="closeQuickCategoryModal" style="padding: 8px 16px; border: 1px solid #cbd5e1; border-radius: 6px; background: #f1f5f9; color: #334155; font-weight: 600; cursor: pointer;">Annuler</button>
              <button type="submit" class="btn btn-success" :disabled="quickCategoryLoading" style="padding: 8px 16px; border: none; border-radius: 6px; background: #10b981; color: white; font-weight: 600; cursor: pointer;">
                <span v-if="quickCategoryLoading"><i class="fas fa-spinner fa-spin"></i></span>
                Créer
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import api from '@/services/api'
import { useToast } from 'vue-toastification'

export default {
  name: 'ProductModal',
  props: {
    product: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'create',
      validator: value => ['create', 'edit'].includes(value)
    }
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const loading = ref(false)
    const errors = reactive({})
    const currentStep = ref(0)
    const completedSteps = ref(new Set())
    const categories = ref([])
    const loadingCategories = ref(false)

    // Quick Add Category Logic
    const toast = useToast()
    const showQuickCategoryModal = ref(false)
    const quickCategoryForm = reactive({
      nom: '',
      emoji: '📦',
      description: ''
    })
    const quickCategoryLoading = ref(false)

    const openQuickCategoryModal = () => {
      quickCategoryForm.nom = ''
      quickCategoryForm.emoji = '📦'
      quickCategoryForm.description = ''
      showQuickCategoryModal.value = true
    }

    const closeQuickCategoryModal = () => {
      showQuickCategoryModal.value = false
    }

    const handleQuickCategorySubmit = async () => {
      if (!quickCategoryForm.nom.trim()) {
        toast.error('Le nom de la catégorie est requis')
        return
      }

      quickCategoryLoading.value = true
      try {
        await api.post('/categories', {
          nom: quickCategoryForm.nom,
          emoji: quickCategoryForm.emoji,
          description: quickCategoryForm.description,
          couleur: '#10b981', // Vert d'Inveep
          actif: true,
          afficherDansProspects: true,
          ordre: 0
        })
        toast.success('Catégorie ajoutée avec succès')
        await loadCategories()
        form.categorie = quickCategoryForm.nom.trim()
        closeQuickCategoryModal()
      } catch (error) {
        console.error(error)
        const errMsg = error.response?.data?.message || 'Erreur lors de la création de la catégorie'
        toast.error(errMsg)
      } finally {
        quickCategoryLoading.value = false
      }
    }
    
    const form = reactive({
      referenceInterne: '',
      designation: '',
      categorie: '',
      marque: '',
      modele: '',
      description: '',
      imei: '',
      iccid: '',
      numeroSerie: '',
      prixAchat: 0,
      prixVente: 0,
      tva: 19,
      quantite: 1,
      seuilAlerte: 5,
      unite: 'pièce',
      emplacement: 'magasin_central',
      fournisseur: ''
    })

    // Configuration des étapes (3 étapes au lieu de 4)
    const steps = ref([
      {
        title: 'Informations & Identification',
        subtitle: 'Données de base',
        icon: 'fas fa-info-circle',
        fields: ['referenceInterne', 'designation', 'categorie', 'marque', 'modele', 'description', 'imei', 'iccid', 'numeroSerie']
      },
      {
        title: 'Informations Financières',
        subtitle: 'Prix et TVA',
        icon: 'fas fa-euro-sign',
        fields: ['prixAchat', 'prixVente', 'tva']
      },
      {
        title: 'Gestion de Stock',
        subtitle: 'Paramètres stock',
        icon: 'fas fa-warehouse',
        fields: ['seuilAlerte', 'unite', 'emplacement', 'fournisseur']
      }
    ])

    // Computed pour déterminer si le numéro de série doit être affiché
    const shouldShowNumeroSerie = computed(() => {
      const categoriesToHide = ['Accessoire', 'Câble', 'Capteur', 'Alimentation', 'Divers']
      return !categoriesToHide.includes(form.categorie)
    })

    // Computed pour déterminer si le champ quantité doit être affiché
    const shouldShowQuantite = computed(() => {
      const categoriesToShow = ['Accessoire', 'Câble', 'Capteur', 'Alimentation', 'Divers']
      return categoriesToShow.includes(form.categorie)
    })

    // Prix TTC calculé
    const prixVenteTTC = computed(() => {
      return form.prixVente * (1 + form.tva / 100)
    })

    // Progression
    const progressPercentage = computed(() => {
      return ((currentStep.value + 1) / steps.value.length) * 100
    })

    // État de validation
    const canProceedToNext = ref(true)
    const isFormValid = ref(true)

    // Fonction pour mettre à jour l'état de validation
    const updateValidationState = (showErrors = false) => {
      canProceedToNext.value = validateCurrentStep(showErrors)
      isFormValid.value = validateFormComplete()
    }

    // Initialisation du formulaire
    const initForm = () => {
      if (props.mode === 'edit' && props.product) {
        Object.keys(form).forEach(key => {
          if (props.product[key] !== undefined) {
            form[key] = props.product[key]
          }
        })
      }
    }

    // Validation par étape
    const validateCurrentStep = (showErrors = true) => {
      const currentStepFields = steps.value[currentStep.value].fields
      let isValid = true
      
      // Nettoyer les erreurs de l'étape actuelle
      if (showErrors) {
        currentStepFields.forEach(field => {
          if (errors[field]) delete errors[field]
        })
      }
      
      // Validation étape 1: Informations Générales & Identification
      if (currentStep.value === 0) {
        // La référence interne est générée automatiquement, pas de validation requise en mode création
        if (!form.designation.trim()) {
          if (showErrors) errors.designation = 'La désignation est requise'
          isValid = false
        }
        if (!form.categorie) {
          if (showErrors) errors.categorie = 'La catégorie est requise'
          isValid = false
        }
        
        // Validation IMEI/ICCID dans la même étape
        if (form.categorie === 'GPS' && !form.imei) {
          if (showErrors) errors.imei = 'IMEI requis pour les GPS'
          isValid = false
        } else if (form.imei && !/^[0-9]{15}$/.test(form.imei)) {
          if (showErrors) errors.imei = 'IMEI doit contenir exactement 15 chiffres'
          isValid = false
        }
        
        if (form.categorie === 'SIM' && !form.iccid) {
          if (showErrors) errors.iccid = 'ICCID requis pour les SIM'
          isValid = false
        } else if (form.iccid && !/^[0-9]{19,20}$/.test(form.iccid)) {
          if (showErrors) errors.iccid = 'ICCID doit contenir 19-20 chiffres'
          isValid = false
        }
      }
      
      // Validation étape 2: Informations Financières
      if (currentStep.value === 1) {
        if (form.prixAchat < 0) {
          if (showErrors) errors.prixAchat = 'Le prix d\'achat ne peut pas être négatif'
          isValid = false
        }
        if (form.prixVente < 0) {
          if (showErrors) errors.prixVente = 'Le prix de vente ne peut pas être négatif'
          isValid = false
        }
        if (form.prixVente < form.prixAchat) {
          if (showErrors) errors.prixVente = 'Le prix de vente doit être supérieur ou égal au prix d\'achat'
          isValid = false
        }
      }
      
      // Validation étape 3: Gestion de Stock
      if (currentStep.value === 2) {
        if (form.quantite < 1) {
          if (showErrors) errors.quantite = 'La quantité doit être au moins 1'
          isValid = false
        }
        if (form.seuilAlerte < 0) {
          if (showErrors) errors.seuilAlerte = 'Le seuil d\'alerte ne peut pas être négatif'
          isValid = false
        }
      }
      
      return isValid
    }

    // Validation complète du formulaire (sans modification de currentStep)
    const validateFormComplete = () => {
      let isValid = true
      
      // Validation étape 1: Informations Générales (référence interne auto-générée)
      if (!form.designation.trim() || !form.categorie) {
        isValid = false
      }
      
      // Validation étape 2: Identification
      if (form.categorie === 'GPS' && !form.imei) {
        isValid = false
      } else if (form.imei && !/^[0-9]{15}$/.test(form.imei)) {
        isValid = false
      }
      
      if (form.categorie === 'SIM' && !form.iccid) {
        isValid = false
      } else if (form.iccid && !/^[0-9]{19,20}$/.test(form.iccid)) {
        isValid = false
      }
      
      // Validation étape 3: Informations Financières
      if (form.prixAchat < 0 || form.prixVente < 0 || form.prixVente < form.prixAchat) {
        isValid = false
      }
      
      // Validation Gestion de Stock
      if (form.quantite < 1 || form.seuilAlerte < 0) {
        isValid = false
      }
      
      return isValid
    }

    // Méthodes
    const onCategorieChange = () => {
      // Réinitialiser les champs spécifiques à la catégorie
      if (form.categorie !== 'GPS') {
        form.imei = ''
      }
      if (form.categorie !== 'SIM') {
        form.iccid = ''
      }
      
      // Forcer quantité = 1 pour GPS et SIM
      if (form.categorie === 'GPS' || form.categorie === 'SIM') {
        form.quantite = 1
      }
    }

    const calculateTTC = () => {
      // Le calcul est fait automatiquement via computed
    }

    // Fonction de validation complète du formulaire
    const validateForm = () => {
      // Nettoyer toutes les erreurs
      Object.keys(errors).forEach(key => delete errors[key])
      
      let isValid = true
      
      // Validation des champs obligatoires (référence interne auto-générée par le backend)
      if (!form.designation.trim()) {
        errors.designation = 'La désignation est requise'
        isValid = false
      }
      
      if (!form.categorie) {
        errors.categorie = 'La catégorie est requise'
        isValid = false
      }
      
      // Validation IMEI pour GPS
      if (form.categorie === 'GPS') {
        if (!form.imei) {
          errors.imei = 'IMEI requis pour les GPS'
          isValid = false
        } else if (!/^[0-9]{15}$/.test(form.imei)) {
          errors.imei = 'IMEI doit contenir exactement 15 chiffres'
          isValid = false
        }
      }
      
      // Validation ICCID pour SIM
      if (form.categorie === 'SIM') {
        if (!form.iccid) {
          errors.iccid = 'ICCID requis pour les SIM'
          isValid = false
        } else if (!/^[0-9]{19,20}$/.test(form.iccid)) {
          errors.iccid = 'ICCID doit contenir 19-20 chiffres'
          isValid = false
        }
      }
      
      // Validation des prix
      if (form.prixAchat < 0) {
        errors.prixAchat = 'Le prix d\'achat ne peut pas être négatif'
        isValid = false
      }
      
      if (form.prixVente < 0) {
        errors.prixVente = 'Le prix de vente ne peut pas être négatif'
        isValid = false
      }
      
      if (form.prixVente < form.prixAchat) {
        errors.prixVente = 'Le prix de vente doit être supérieur ou égal au prix d\'achat'
        isValid = false
      }
      
      // Validation quantité
      if (form.quantite < 1) {
        errors.quantite = 'La quantité doit être au moins 1'
        isValid = false
      }
      
      // Validation seuil d'alerte
      if (form.seuilAlerte < 0) {
        errors.seuilAlerte = 'Le seuil d\'alerte ne peut pas être négatif'
        isValid = false
      }
      
      return isValid
    }

    const handleSubmit = async () => {
      if (!validateForm()) {
        return
      }

      loading.value = true
      try {
        const productData = { ...form }
        emit('save', productData)
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      } finally {
        loading.value = false
      }
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'TND'
      }).format(price || 0)
    }

    // Navigation entre les étapes
    const goToStep = (stepIndex) => {
      if (stepIndex < currentStep.value || isStepCompleted(stepIndex)) {
        currentStep.value = stepIndex
        updateValidationState(false)
      }
    }

    const nextStep = () => {
      if (validateCurrentStep(true)) {
        completedSteps.value.add(currentStep.value)
        if (currentStep.value < steps.value.length - 1) {
          currentStep.value++
        }
        updateValidationState(false)
      }
    }

    const previousStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--
        updateValidationState(false)
      }
    }

    const isStepCompleted = (stepIndex) => {
      return completedSteps.value.has(stepIndex)
    }

    const getStepClass = (stepIndex) => {
      const classes = []
      if (stepIndex === currentStep.value) classes.push('active')
      if (isStepCompleted(stepIndex)) classes.push('completed')
      if (stepIndex < currentStep.value) classes.push('clickable')
      return classes.join(' ')
    }

    // Watchers
    watch(() => props.product, initForm, { immediate: true })
    
    // Watcher pour mettre à jour la validation quand les champs changent
    watch([
      () => form.referenceInterne,
      () => form.designation,
      () => form.categorie,
      () => form.imei,
      () => form.iccid,
      () => form.prixAchat,
      () => form.prixVente,
      () => form.quantite,
      () => form.seuilAlerte
    ], () => {
      updateValidationState(false)
    })

    // Charger les catégories depuis l'API
    const loadCategories = async () => {
      loadingCategories.value = true
      try {
        console.log('🔍 Chargement des catégories...')
        const response = await api.get('/categories', {
          params: { actif: true }
        })
        console.log('✅ Catégories reçues:', response.data)
        categories.value = response.data.data
        console.log('📦 Catégories chargées:', categories.value.length)
      } catch (error) {
        console.error('❌ Erreur chargement catégories:', error)
        console.error('Détails:', error.response?.data || error.message)
      } finally {
        loadingCategories.value = false
      }
    }

    onMounted(() => {
      initForm()
      updateValidationState(false)
      loadCategories()
    })

    return {
      loading,
      errors,
      form,
      currentStep,
      completedSteps,
      steps,
      categories,
      loadingCategories,
      prixVenteTTC,
      progressPercentage,
      canProceedToNext,
      isFormValid,
      shouldShowNumeroSerie,
      shouldShowQuantite,
      onCategorieChange,
      calculateTTC,
      validateForm,
      handleSubmit,
      formatPrice,
      goToStep,
      nextStep,
      previousStep,
      isStepCompleted,
      getStepClass,
      validateCurrentStep,
      updateValidationState,
      showQuickCategoryModal,
      quickCategoryForm,
      quickCategoryLoading,
      openQuickCategoryModal,
      closeQuickCategoryModal,
      handleQuickCategorySubmit
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
  max-width: 1100px;
  width: 100%;
  max-height: 95vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1), transparent);
  z-index: 0;
}

.modal-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 600;
  position: relative;
  z-index: 1;
  letter-spacing: 0.5px;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.3s ease;
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
}

.form-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-section {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 27, 183, 0.1);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.form-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.02), rgba(0, 70, 255, 0.01));
  z-index: 0;
}

.form-section > * {
  position: relative;
  z-index: 1;
}

.form-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 27, 183, 0.12);
  border-color: rgba(0, 27, 183, 0.2);
}

.section-title {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-blue);
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(0, 27, 183, 0.15);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.3px;
}

.section-title::before {
  content: '';
  width: 4px;
  height: 24px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 2px;
  flex-shrink: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 0.4rem;
  opacity: 0.9;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem 1rem;
  border: 2px solid rgba(0, 27, 183, 0.12);
  border-radius: 12px;
  font-size: 0.85rem;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.04);
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.08), 0 8px 25px rgba(0, 27, 183, 0.1);
  background: white;
  transform: translateY(-2px) scale(1.02);
}

/* Premium Selector Ultra-Professional Design */
.premium-selector-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
  backdrop-filter: blur(20px);
  border: 2px solid rgba(0, 27, 183, 0.15);
  border-radius: 18px;
  padding: 0;
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 10px 40px rgba(0, 27, 183, 0.08);
  overflow: hidden;
  min-height: 56px;
}

.premium-selector-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.03), rgba(0, 70, 255, 0.02));
  z-index: 0;
}

.premium-selector-wrapper:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 50px rgba(0, 27, 183, 0.15);
  border-color: rgba(0, 27, 183, 0.25);
}

.premium-selector-wrapper:focus-within {
  transform: translateY(-4px);
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.12), 0 20px 60px rgba(0, 27, 183, 0.2);
}

.premium-selector-wrapper:focus-within .selector-arrow {
  transform: rotate(180deg);
  color: var(--secondary-blue);
}

.premium-selector-wrapper.error {
  border-color: #dc3545;
  box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.15);
}

.selector-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-radius: 14px;
  margin: 2px;
  font-size: 1.1rem;
  position: relative;
  z-index: 2;
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.3);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.premium-selector-wrapper.has-selection .selector-icon-container {
  background: linear-gradient(135deg, #10b981, #059669);
  animation: pulse 2s infinite;
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
}

.premium-select {
  flex: 1;
  background: transparent !important;
  border: none !important;
  padding: 0.875rem 1.25rem !important;
  font-size: 0.9rem;
  color: var(--primary-blue);
  font-weight: 600;
  position: relative;
  z-index: 1;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  letter-spacing: 0.3px;
}

.premium-select:focus {
  outline: none;
  box-shadow: none !important;
  transform: none !important;
  border: none !important;
}

.premium-selector-wrapper.has-selection .premium-select {
  color: var(--secondary-blue);
  font-weight: 700;
}

.premium-select option {
  background: white;
  color: #333;
  padding: 0.875rem;
  font-size: 0.9rem;
  border-radius: 10px;
  margin: 3px 0;
  font-weight: 500;
}

.premium-select option:first-child {
  color: #6c757d;
  font-style: italic;
  font-weight: 400;
}

.premium-select option:not(:first-child) {
  font-weight: 600;
}

.premium-select option:hover {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.08), rgba(0, 70, 255, 0.04));
}

.selector-arrow-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: var(--primary-blue);
  font-size: 1rem;
  position: relative;
  z-index: 2;
  margin-right: 8px;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.selector-arrow {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.premium-selector-wrapper.has-selection .selector-arrow-container {
  color: var(--secondary-blue);
}

.selection-indicator {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.8rem;
  z-index: 3;
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  animation: pulse 2s infinite;
  border: 2px solid white;
}

.selection-badge {
  position: absolute;
  top: -8px;
  right: 10px;
  width: 22px;
  height: 22px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  z-index: 4;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
  animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  border: 2px solid white;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@keyframes bounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes shine {
  0% { left: -100%; }
  100% { left: 100%; }
}

/* Selector Shine Effect */
.selector-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
  transition: left 0.8s ease;
  z-index: 1;
  pointer-events: none;
}

.premium-selector-wrapper:hover .selector-shine {
  left: 100%;
}

.form-group input.error,
.form-group select.error,
.form-group textarea.error {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.error-message {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.help-text {
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-style: italic;
  padding: 0.5rem;
  background: rgba(108, 117, 125, 0.05);
  border-radius: 6px;
  border-left: 3px solid rgba(108, 117, 125, 0.3);
}

/* TVA Input Wrapper */
.tva-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.tva-input-wrapper input {
  padding-right: 2.5rem !important;
  flex: 1;
}

.tva-symbol {
  position: absolute;
  right: 1rem;
  color: var(--primary-blue);
  font-weight: 700;
  font-size: 0.9rem;
  pointer-events: none;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Badges catégories */
.category-badge, .status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Badges catégories */
.category-gps { background: #e3f2fd; color: #1976d2; }
.category-sim { background: #f3e5f5; color: #7b1fa2; }
.category-accessoire { background: #e8f5e8; color: #388e3c; }
.category-cable { background: #fff3e0; color: #f57c00; }
.category-capteur { background: #fce4ec; color: #c2185b; }
.category-alimentation { background: #e0f2f1; color: #00796b; }
.category-divers { background: #f5f5f5; color: #616161; }

/* Badges statuts */
.status-available { background: #d4edda; color: #155724; }
.status-assigned { background: #cce5ff; color: #004085; }
.status-installed { background: #d1ecf1; color: #0c5460; }
.status-returned { background: #f8d7da; color: #721c24; }
.status-defective { background: #f5c6cb; color: #721c24; }
.status-lost { background: #343a40; color: white; }

.calculated-value {
  padding: 0.875rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.06), rgba(0, 70, 255, 0.03));
  border: 2px solid rgba(0, 27, 183, 0.12);
  border-radius: 12px;
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 1rem;
  text-align: center;
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.08);
  position: relative;
  overflow: hidden;
  animation: valueGlow 2s ease-in-out infinite alternate;
}

@keyframes valueGlow {
  from { box-shadow: 0 6px 20px rgba(0, 27, 183, 0.08); }
  to { box-shadow: 0 8px 25px rgba(0, 27, 183, 0.15); }
}

.calculated-value::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.3), transparent);
  z-index: 0;
}

.calculated-value span {
  position: relative;
  z-index: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
  backdrop-filter: blur(10px);
}

/* Stepper Navigation */
.stepper-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95));
  backdrop-filter: blur(15px);
  border-radius: 20px;
  border: 1px solid rgba(0, 27, 183, 0.08);
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.06);
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stepper-navigation::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.02), rgba(0, 70, 255, 0.01));
  z-index: 0;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  z-index: 1;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  padding: 0.5rem 0.75rem;
  border-radius: 16px;
  flex: 1;
  min-width: 0;
}

.step-item.clickable:hover {
  background: rgba(0, 27, 183, 0.05);
  transform: translateY(-2px);
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

.step-item .step-circle {
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  color: #64748b;
  border: 2px solid #e2e8f0;
}

.step-item.active .step-circle {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: 2px solid var(--primary-blue);
  box-shadow: 0 4px 16px rgba(0, 27, 183, 0.3);
  animation: pulse 2s infinite;
}

.step-item.completed .step-circle {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: 2px solid #10b981;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  flex: 1;
}

.step-title {
  font-weight: 600;
  font-size: 0.75rem;
  color: var(--primary-blue);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.step-item.active .step-title {
  color: var(--secondary-blue);
}

.step-item.completed .step-title {
  color: #059669;
}

.step-subtitle {
  font-size: 0.65rem;
  color: #64748b;
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.step-connector {
  flex: 0 0 30px;
  height: 3px;
  background: linear-gradient(90deg, #e2e8f0, #cbd5e1);
  margin: 0 0.5rem;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
  animation: connectorGlow 2s ease-in-out infinite alternate;
}

@keyframes connectorGlow {
  from { opacity: 0.6; }
  to { opacity: 1; }
}

.step-item.completed + .step-item .step-connector::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, #10b981, #059669);
  animation: fillConnector 0.5s ease-out;
}

@keyframes fillConnector {
  from { width: 0; }
  to { width: 100%; }
}

/* Step Headers */
.step-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.02));
  border-radius: 16px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  position: relative;
  overflow: hidden;
}

.step-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1), transparent);
  z-index: 0;
}

.step-icon-large {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 10px 30px rgba(0, 27, 183, 0.25);
  position: relative;
  z-index: 1;
  animation: iconPulse 3s ease-in-out infinite;
}

@keyframes iconPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.step-info {
  position: relative;
  z-index: 1;
}

.step-description {
  margin: 0.3rem 0 0 0;
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.3;
  font-style: italic;
  opacity: 0.9;
}

/* Active Step Animation */
.active-step {
  animation: stepSlideIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes stepSlideIn {
  from {
    opacity: 0;
    transform: translateX(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

/* Step Navigation */
.step-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95));
  backdrop-filter: blur(15px);
  border-radius: 20px;
  border: 1px solid rgba(0, 27, 183, 0.08);
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.06);
  gap: 1rem;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.step-progress {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin: 0 2rem;
}

.progress-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--primary-blue);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.9;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #e2e8f0, #cbd5e1);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 4px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.step-btn {
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 0.75rem;
  position: relative;
  overflow: hidden;
  min-width: 120px;
  justify-content: center;
}

.btn {
  padding: 0.875rem 1.75rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.9rem;
  position: relative;
  overflow: hidden;
}

.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.2), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 0;
}

.btn:hover::before {
  opacity: 1;
}

.btn > * {
  position: relative;
  z-index: 1;
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: 1px solid rgba(0, 27, 183, 0.3);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.2);
}

.btn-secondary {
  background: linear-gradient(135deg, #6c757d, #495057);
  color: white;
  border: 1px solid rgba(108, 117, 125, 0.3);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.2);
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: 1px solid rgba(16, 185, 129, 0.3);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.btn-success:hover:not(:disabled) {
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 8px 20px rgba(0, 27, 183, 0.3);
}

.btn-secondary:hover:not(:disabled) {
  box-shadow: 0 8px 20px rgba(108, 117, 125, 0.3);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 1200px) {
  .step-content {
    display: none;
  }
  
  .step-item {
    justify-content: center;
  }
  
  .step-connector {
    flex: 0 0 20px;
  }
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }
  
  .modal-content {
    max-height: 98vh;
    border-radius: 16px;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-title {
    font-size: 1rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .form-section {
    padding: 1rem;
  }
  
  .stepper-navigation {
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 1rem;
  }
  
  .step-item {
    flex: 1;
    min-width: calc(25% - 0.375rem);
    flex-direction: column;
    text-align: center;
    gap: 0.3rem;
  }
  
  .step-content {
    display: none;
  }
  
  .step-connector {
    display: none;
  }
  
  .step-navigation {
    flex-direction: column;
    gap: 1rem;
  }
  
  .step-progress {
    margin: 0;
    order: -1;
  }
  
  .step-btn {
    width: 100%;
    justify-content: center;
    font-size: 0.8rem;
  }
  
  .step-header {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }
  
  .step-icon-large {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
  
  /* Premium Selector Responsive */
  .premium-selector-wrapper {
    min-height: 50px;
  }
  
  .selector-icon-container {
    width: 46px;
    height: 46px;
    font-size: 1rem;
  }
  
  .premium-select {
    font-size: 0.85rem;
    padding: 0.75rem 1rem !important;
  }
  
  .selector-arrow-container {
    width: 40px;
    height: 40px;
    font-size: 0.9rem;
  }
  
  .selection-indicator {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
    top: -8px;
    right: -8px;
  }
  
  .selection-badge {
    width: 20px;
    height: 20px;
    font-size: 0.65rem;
    top: -6px;
    right: 8px;
  }
}

@media (max-width: 480px) {
  .premium-selector-wrapper {
    min-height: 46px;
    border-radius: 14px;
  }
  
  .selector-icon-container {
    width: 42px;
    height: 42px;
    font-size: 0.9rem;
    border-radius: 12px;
  }
  
  .premium-select {
    font-size: 0.8rem;
    padding: 0.65rem 0.9rem !important;
  }
  
  .selector-arrow-container {
    width: 36px;
    height: 36px;
    font-size: 0.85rem;
    margin-right: 6px;
  }
  
  .selection-indicator {
    width: 22px;
    height: 22px;
    font-size: 0.65rem;
    top: -6px;
    right: -6px;
  }
  
  .selection-badge {
    width: 18px;
    height: 18px;
    font-size: 0.6rem;
    top: -4px;
    right: 6px;
  }
}

/* Styles pour le champ auto-généré */
.auto-generated-field {
  position: relative;
  display: flex;
  align-items: center;
}

.auto-generated-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  background-color: #f8fafc;
  color: #64748b;
  cursor: not-allowed;
}

.auto-generated-input::placeholder {
  color: #94a3b8;
  font-style: italic;
}

.auto-badge {
  position: absolute;
  right: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.auto-badge i {
  font-size: 0.7rem;
}
</style>
